<template>
  <div class="mt-4 flex flex-col gap-3" style="height: calc(100vh - 200px)">
    <!-- Actions -->
    <div class="flex justify-end gap-2 shrink-0">
      <el-button @click="handleReset">{{ t('resetToDefault') }}</el-button>
      <el-button type="primary" @click="handleSave">{{ t('save') }}</el-button>
    </div>

    <!-- Two-column body -->
    <div class="flex gap-4 min-h-0 flex-1">

      <!-- Left: Current Setup -->
      <div class="flex flex-col w-2/5 min-h-0">
        <div class="mb-1 text-sm font-semibold text-gray-500 uppercase tracking-wide shrink-0">
          {{ t('currentSetup') }}
        </div>
        <div class="flex-1 overflow-y-auto rounded-lg border border-dashed border-[#1D4C75] bg-[#1D4C750A] p-3 min-h-0">
          <draggable
            v-model="activeDefs"
            item-key="id"
            class="grid grid-cols-3 gap-3"
            ghost-class="opacity-20"
            @end="onDragEnd"
          >
            <template #item="{ element }">
              <div class="relative group cursor-grab">
                <div class="home-btn" :class="btnColorClass(element)">
                  <span v-if="element.icon === 'icon-examination'" class="text-2xl mb-1">
                    <i class="icon-examination" />
                  </span>
                  <span v-else-if="element.icon === 'return-sale'" class="mb-1">
                    <CustomIcon width="1.5rem" height="1.5rem"><IReturnSale /></CustomIcon>
                  </span>
                  <span v-else-if="element.image" class="mb-1 flex items-center justify-center">
                    <img :src="element.image" alt="" class="h-6" />
                  </span>
                  <span v-else-if="element.iconify" class="text-2xl mb-1 flex items-center justify-center">
                    <Icon :icon="element.iconify" />
                  </span>
                  <span v-else class="text-2xl mb-1">
                    <font-awesome-icon :icon="[element.iconPrefix || 'fas', element.icon]" />
                  </span>
                  <div class="text-xs font-medium text-center leading-tight px-1">{{ $t(element.title) }}</div>
                </div>
                <button class="remove-badge" @click.stop="removeBtn(element.id)">×</button>
              </div>
            </template>
          </draggable>
          <div v-if="activeDefs.length === 0" class="flex items-center justify-center h-20 text-sm text-gray-400">
            {{ t('noButtons') }}
          </div>
        </div>
      </div>

      <!-- Right: Available Buttons -->
      <div class="flex flex-col flex-1 min-h-0">
        <div class="mb-1 text-sm font-semibold text-gray-500 uppercase tracking-wide shrink-0">
          {{ t('availableButtons') }}
        </div>
        <div class="flex-1 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50 p-3 min-h-0">
          <div v-for="(buttons, category) in catalogByCategory" :key="category" class="mb-5 last:mb-0">
            <div class="flex items-center gap-2 mb-3">
              <span class="text-xs font-bold text-[#1D4C75] uppercase tracking-wider whitespace-nowrap">
                {{ t('categories.' + category) }}
              </span>
              <div class="flex-1 border-t border-gray-200"></div>
            </div>
            <div class="grid grid-cols-3 gap-3">
              <div
                v-for="btn in buttons"
                :key="btn.id"
                class="relative"
                :class="isActive(btn.id) ? 'opacity-40 pointer-events-none' : 'cursor-pointer group'"
                @click="!isActive(btn.id) ? addBtn(btn.id) : undefined"
              >
                <div class="home-btn" :class="btnColorClass(btn)">
                  <span v-if="btn.icon === 'icon-examination'" class="text-2xl mb-1">
                    <i class="icon-examination" />
                  </span>
                  <span v-else-if="btn.icon === 'return-sale'" class="mb-1">
                    <CustomIcon width="1.5rem" height="1.5rem"><IReturnSale /></CustomIcon>
                  </span>
                  <span v-else-if="btn.image" class="mb-1 flex items-center justify-center">
                    <img :src="btn.image" alt="" class="h-6" />
                  </span>
                  <span v-else-if="btn.iconify" class="text-2xl mb-1 flex items-center justify-center">
                    <Icon :icon="btn.iconify" />
                  </span>
                  <span v-else class="text-2xl mb-1">
                    <font-awesome-icon :icon="[btn.iconPrefix || 'fas', btn.icon]" />
                  </span>
                  <div class="text-xs font-medium text-center leading-tight px-1">{{ $t(btn.title) }}</div>
                </div>
                <button
                  v-if="!isActive(btn.id)"
                  class="add-badge opacity-0 group-hover:opacity-100 transition-opacity"
                  @click.stop="addBtn(btn.id)"
                >+</button>
                <div v-else class="absolute inset-0 rounded-lg flex items-center justify-center bg-white/60">
                  <span class="text-[10px] font-bold text-[#1D4C75] uppercase tracking-wide">{{ t('added') }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import draggable from 'vuedraggable'
import { ElMessage } from 'element-plus'
import { useHomeScreen, ALL_BUTTONS, buttonFlagsEnabled } from '@/composables/useHomeScreen'
import { useSettingsStore } from '@/stores/settingsStore'
import CustomIcon from '@/components/CustomIcon.vue'
import IReturnSale from '@/components/icons/sales/IReturnSale.vue'
import { Icon } from '@iconify/vue'

const CATEGORY_ORDER = ['sales', 'inventory', 'customers', 'companies', 'hr', 'accounting', 'examinations', 'vets']

export default defineComponent({
  components: { draggable, CustomIcon, IReturnSale, Icon },

  setup() {
    const { t } = useI18n({
      inheritLocale: true,
      messages: {
        en: {
          currentSetup: 'Current Setup',
          availableButtons: 'Available Buttons',
          save: 'Save',
          resetToDefault: 'Reset to Default',
          savedSuccess: 'Home screen saved successfully',
          resetSuccess: 'Home screen reset to default',
          noButtons: 'No buttons selected. Add some from below.',
          maxButtons: 'Maximum 9 buttons allowed',
          added: 'Added',
          categories: {
            sales: 'Sales',
            inventory: 'Inventory',
            customers: 'Customers',
            companies: 'Companies',
            hr: 'HR',
            accounting: 'Accounting',
            examinations: 'Examinations',
            vets: 'Vets'
          }
        },
        ar: {
          currentSetup: 'الإعداد الحالي',
          availableButtons: 'الأزرار المتاحة',
          save: 'حفظ',
          resetToDefault: 'إعادة الضبط',
          savedSuccess: 'تم حفظ الشاشة الرئيسية بنجاح',
          resetSuccess: 'تمت إعادة ضبط الشاشة الرئيسية إلى الإعداد الافتراضي',
          noButtons: 'لم يتم تحديد أي أزرار. أضف بعضها من الأسفل.',
          maxButtons: 'الحد الأقصى 9 أزرار',
          added: 'مضاف',
          categories: {
            sales: 'المبيعات',
            inventory: 'المخزون',
            customers: 'العملاء',
            companies: 'الشركات',
            hr: 'الموارد البشرية',
            accounting: 'المحاسبة',
            examinations: 'الفحوصات',
            vets: 'الطب البيطري'
          }
        }
      }
    })

    const homeScreen = useHomeScreen()
    const settingsStore = useSettingsStore()

    function filterByFlag(defs: typeof ALL_BUTTONS) {
      return defs.filter(btn => buttonFlagsEnabled(btn, key => settingsStore.getValue(key)))
    }

    const activeDefs = ref<typeof ALL_BUTTONS>(filterByFlag([...homeScreen.activeButtonDefs.value]))

    watch(homeScreen.activeButtonDefs, (defs) => {
      activeDefs.value = filterByFlag([...defs])
    })

    function isActive(id: number) {
      return activeDefs.value.some(b => b.id === id)
    }

    function addBtn(id: number) {
      if (!isActive(id)) {
        if (activeDefs.value.length >= 9) {
          ElMessage.warning(t('maxButtons'))
          return
        }
        const btn = ALL_BUTTONS.find(b => b.id === id)
        if (btn) activeDefs.value = [...activeDefs.value, btn]
      }
    }

    function removeBtn(id: number) {
      activeDefs.value = activeDefs.value.filter(b => b.id !== id)
    }

    function onDragEnd() {}

    function handleSave() {
      homeScreen.reorder(activeDefs.value.map(b => b.id))
      homeScreen.save()
      ElMessage.success(t('savedSuccess'))
    }

    function handleReset() {
      homeScreen.resetToDefault()
      ElMessage.success(t('resetSuccess'))
    }

    const catalogByCategory = computed(() => {
      const result: Record<string, typeof ALL_BUTTONS> = {}
      for (const cat of CATEGORY_ORDER) {
        const buttons = ALL_BUTTONS.filter(btn => {
          if (btn.category !== cat) return false
          if (!buttonFlagsEnabled(btn, key => settingsStore.getValue(key))) return false
          return true
        })
        if (buttons.length > 0) result[cat] = buttons
      }
      return result
    })

    function btnColorClass(btn: typeof ALL_BUTTONS[0]) {
      if (btn.isSuccess) return 'home-btn-success'
      if (btn.isDanger) return 'home-btn-danger'
      return 'home-btn-default'
    }

    return {
      t,
      activeDefs,
      catalogByCategory,
      isActive,
      addBtn,
      removeBtn,
      onDragEnd,
      handleSave,
      handleReset,
      btnColorClass
    }
  }
})
</script>

<style scoped>
.home-btn {
  @apply rounded-lg shadow-sm flex flex-col items-center justify-center py-3 gap-1 min-h-[100px] w-full select-none;
}

.home-btn-default {
  background: var(--secondary-bg-color);
  color: var(--secondary-text-color);
}

.home-btn-success {
  background: var(--success-bg-color);
  color: var(--success-text-color);
}

.home-btn-danger {
  background: var(--danger-bg-color);
  color: var(--danger-text-color);
}

.remove-badge {
  @apply absolute -top-2 -end-2 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center text-sm font-bold shadow z-10 leading-none;
  @apply opacity-0 group-hover:opacity-100 transition-opacity;
}

.add-badge {
  @apply absolute -top-2 -end-2 w-5 h-5 rounded-full bg-[#1D4C75] text-white flex items-center justify-center text-sm font-bold shadow z-10 leading-none;
}
</style>
